<template>
  <v-card flat>
    <v-card-title>
      <div class="d-flex align-center flex-wrap">
        <v-btn-toggle
          v-if="isAdmin"
          v-model="mode"
          mandatory
          dense
          class="mr-3"
        >
          <v-btn small value="week">Week</v-btn>
          <v-btn small value="weeks">Weeks</v-btn>
          <v-btn small value="month">Month</v-btn>
        </v-btn-toggle>

        <v-btn icon small @click="shiftSelection(-1)" :disabled="!dateFrom" title="Previous">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-menu
          v-if="mode === 'week'"
          v-model="showWeekPicker"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="dateFrom ? `Week of ${dateFormatted(dateFrom, {formatString: 'MMM D, YYYY'})}` : ''"
              label="Invoice week"
              hint="Weeks start on Sunday"
              persistent-hint
              readonly
              v-on="on"
              append-icon="mdi-calendar"
              :loading="loadingSummary"
              style="max-width: 220px"
              class="mx-1"
            />
          </template>
          <v-date-picker
            :value="dateFrom"
            :allowed-dates="isSunday"
            no-title
            @input="selectWeek"
          />
        </v-menu>

        <template v-if="mode === 'weeks'">
          <v-menu
            v-model="showFromPicker"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="dateFrom ? `Week of ${dateFormatted(dateFrom, {formatString: 'MMM D, YYYY'})}` : ''"
                label="From week"
                hint="Weeks start on Sunday"
                persistent-hint
                readonly
                v-on="on"
                append-icon="mdi-calendar"
                :loading="loadingSummary"
                style="max-width: 190px"
                class="mx-1"
              />
            </template>
            <v-date-picker
              :value="dateFrom"
              :allowed-dates="isSunday"
              no-title
              @input="selectWeeksFrom"
            />
          </v-menu>
          <v-menu
            v-model="showToPicker"
            :close-on-content-click="false"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="dateTo ? `Week of ${dateFormatted(dateTo, {formatString: 'MMM D, YYYY'})}` : ''"
                label="To week"
                :hint="weekCountHint"
                persistent-hint
                readonly
                v-on="on"
                append-icon="mdi-calendar"
                :loading="loadingSummary"
                style="max-width: 190px"
                class="mx-1"
              />
            </template>
            <v-date-picker
              :value="dateTo"
              :allowed-dates="isAllowedToWeek"
              no-title
              @input="selectWeeksTo"
            />
          </v-menu>
        </template>

        <v-menu
          v-if="mode === 'month'"
          v-model="showMonthPicker"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="dateFrom ? monthFormatted : ''"
              label="Invoice month"
              hint="All invoice weeks starting in this month"
              persistent-hint
              readonly
              v-on="on"
              append-icon="mdi-calendar"
              :loading="loadingSummary"
              style="max-width: 220px"
              class="mx-1"
            />
          </template>
          <v-date-picker
            :value="monthValue"
            type="month"
            no-title
            @input="selectMonth"
          />
        </v-menu>

        <v-btn icon small @click="shiftSelection(1)" :disabled="!dateFrom" title="Next">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>

        <v-checkbox
          v-if="isAdmin"
          v-model="compareLastYear"
          label="Compare to last year"
          :hint="compareLastYear && comparisonDatesFormatted ? `vs ${comparisonDatesFormatted}` : ''"
          persistent-hint
          class="ml-4 mt-0"
        />
      </div>
      <v-spacer/>
      <div class="flex-column">
        <DebouncedTextField
          v-if="invoicesVisible"
          label="Search"
          v-model="search"
          clearable
          append-icon="mdi-magnify"
          style="max-width: 200px"
        />
        <v-checkbox
          v-model="showUnconfirmed"
          label="Include Unconfirmed"
        />
      </div>
    </v-card-title>
    <v-card-text v-if="showLoadPrompt" class="text-center py-10">
      <v-btn
        x-large
        color="primary"
        :loading="loadingSummary || loadingComparisonSummary"
        :disabled="!dateFrom"
        @click="loadData"
      >
        <v-icon left>mdi-database-search</v-icon>
        Load {{ datesFormatted }}
      </v-btn>
      <div v-if="compareLastYear && comparisonDatesFormatted" class="text-caption mt-2">
        compared to {{ comparisonDatesFormatted }}
      </div>
      <div v-if="hasData" class="mt-4">
        <v-btn text small @click="cancelLoad">Cancel</v-btn>
        <div class="text-caption grey--text">go back to {{ loadedDatesLabel }}</div>
      </div>
    </v-card-text>
    <template v-else>
    <v-card-text>
      <v-tabs v-model="summaryTab" centered>
        <v-tab>Summary</v-tab>
        <v-tab>By Plan &amp; Stream</v-tab>
      </v-tabs>
      <v-tabs-items v-model="summaryTab" class="pt-3">
        <v-tab-item>
          <div class="summary-tables">
          <v-simple-table
            v-show="!loadingSummary && (!showComparison || !loadingComparisonSummary)"
            dense
            class="comparison-table"
          >
            <thead>
            <tr>
              <th></th>
              <th class="text-right">{{ loadedDatesLabel }}</th>
              <template v-if="showComparison">
                <th class="text-right">Last year ({{ loadedComparisonLabel }})</th>
                <th class="text-right">Change</th>
              </template>
            </tr>
            </thead>
            <tbody>
            <tr v-for="row in comparisonRows" :key="row.label">
              <td>{{ row.label }}</td>
              <td class="text-right">{{ row.current }}</td>
              <template v-if="showComparison">
                <td class="text-right">{{ row.previous }}</td>
                <td class="text-right" :class="row.changeClass">{{ row.change }}</td>
              </template>
            </tr>
            </tbody>
          </v-simple-table>
          <div
            v-if="showPickupSection"
            v-show="!loadingSummary && (!showComparison || !loadingComparisonSummary)"
            class="mt-4"
          >
            <div class="text-subtitle-2 text-center">Staff &amp; Pickup Orders (excluded from table above)</div>
            <v-simple-table dense class="comparison-table">
              <thead>
              <tr>
                <th></th>
                <th class="text-right">{{ loadedDatesLabel }}</th>
                <template v-if="showComparison">
                  <th class="text-right">Last year ({{ loadedComparisonLabel }})</th>
                  <th class="text-right">Change</th>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in pickupComparisonRows" :key="row.label">
                <td>{{ row.label }}</td>
                <td class="text-right">{{ row.current }}</td>
                <template v-if="showComparison">
                  <td class="text-right">{{ row.previous }}</td>
                  <td class="text-right" :class="row.changeClass">{{ row.change }}</td>
                </template>
              </tr>
              </tbody>
            </v-simple-table>
          </div>
          </div>
        </v-tab-item>
        <v-tab-item>
          <v-row v-show="!loadingSummary">
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 text-center mb-1">Sales by Plan</div>
              <v-simple-table dense class="comparison-table">
                <thead>
                <tr>
                  <th>Plan</th>
                  <th class="text-right">Quantity</th>
                  <th class="text-right">Sales</th>
                  <template v-if="showComparison">
                    <th class="text-right">Last Year</th>
                    <th class="text-right">Change</th>
                  </template>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in planSalesRows" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td class="text-right">{{ row.quantity }}</td>
                  <td class="text-right">{{ formatCurrency(row.sales) }}</td>
                  <template v-if="showComparison">
                    <td class="text-right">{{ formatCurrency(row.previousSales) }}</td>
                    <td class="text-right" :class="row.changeClass">{{ row.change }}</td>
                  </template>
                </tr>
                </tbody>
              </v-simple-table>
            </v-col>
            <v-col cols="12" md="6">
              <div class="text-subtitle-2 text-center mb-1">Sales by Stream</div>
              <v-simple-table dense class="comparison-table">
                <thead>
                <tr>
                  <th>Stream</th>
                  <th class="text-right">Quantity</th>
                  <th class="text-right">Sales</th>
                  <template v-if="showComparison">
                    <th class="text-right">Last Year</th>
                    <th class="text-right">Change</th>
                  </template>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in streamSalesRows" :key="row.label">
                  <td>{{ row.label }}</td>
                  <td class="text-right">{{ row.quantity }}</td>
                  <td class="text-right">{{ formatCurrency(row.sales) }}</td>
                  <template v-if="showComparison">
                    <td class="text-right">{{ formatCurrency(row.previousSales) }}</td>
                    <td class="text-right" :class="row.changeClass">{{ row.change }}</td>
                  </template>
                </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
    <v-card-actions>

      <template v-if="invoicesVisible">
      <div v-for="(filter, index) in filters" :key="index" class="mr-2"
           style="display: inline-block; vertical-align: top;">
        <v-card outlined class="pa-2" style="min-width: 200px;">
          <div class="d-flex justify-space-between align-center mb-2">
            <span class="text-caption">Filter {{ index + 1 }}</span>
            <v-btn
              icon
              x-small
              @click="removeFilter(index)"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-select
            label="Column"
            v-model="filter.column"
            single-line
            hide-details
            :items="availableColumnsForFilter(index)"
            dense
            class="mb-2"
          />
          <v-select
            label="Values"
            v-model="filter.values"
            single-line
            hide-details
            multiple
            chips
            clearable
            :items="getFilterValues(filter.column)"
            :disabled="!filter.column"
            dense
          />
        </v-card>
      </div>
      <v-btn
        outlined
        small
        @click="addFilter"
        :disabled="filters.length >= filterableColumns.length"
      >
        <v-icon left>mdi-plus</v-icon>
        Add Filter
      </v-btn>
      </template>
      <v-spacer/>
      <v-btn outlined color="primary" @click="toggleInvoices" :loading="loading">
        <v-icon left>{{ invoicesVisible ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
        {{ showInvoicesLabel }}
      </v-btn>
      <v-btn v-if="invoicesVisible" :disabled="!invoiceItems.length" outlined
             @click="csvExport(dataAsTable,exportFilename())">Download CSV
      </v-btn>
    </v-card-actions>
    <v-card-text v-if="invoicesVisible">
      <v-data-table
        :items="invoiceItems"
        :headers="invoiceItemHeaders"
        dense
        :loading="loading"
        class="invoice-items-table"
      >
        <!-- slot templates define named slots, not a rendered list - no key needed -->
        <!-- eslint-disable vue/valid-v-for -->
        <template v-for="header in invoiceItemHeaders" v-slot:[`item.${header.value}`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                @click="copyToClipboard(item[header.value])"
                style="cursor: pointer;"
              >
                {{ item[header.value] }}
              </span>
            </template>
            <span>{{ item[header.value] }}</span>
          </v-tooltip>
        </template>
        <!-- eslint-enable vue/valid-v-for -->
      </v-data-table>
    </v-card-text>
    </template>
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      color="success"
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import api from "@/api";
import moment from "moment/moment";
import {csvAsTable, csvExport, dateFormatted, formatCurrency} from "@/store/utils";
import urlState from "@/router/urlState";
import DebouncedTextField from "@/components/DebouncedTextField.vue";

const DATE_FORMAT = 'YYYY-MM-DD';

export default {
  name: "Invoices",
  components: {DebouncedTextField},
  props: {
    isAdmin: {type: Boolean, default: false, required: false},
    exportFile: {type: String, default: null, required: false}
  },
  mixins: [urlState],
  data() {
    return {
      summary: null,
      comparisonSummary: null,
      invoiceItemsRaw: [],
      comparisonItemsRaw: [],
      invoicesVisible: false,
      // data is only fetched when the user asks for it, so the selection can be
      // changed freely; `loaded` snapshots what is currently on screen
      loaded: null,
      summaryTab: 0,
      dates: [],
      mode: 'week',
      compareLastYear: false,
      showWeekPicker: false,
      showFromPicker: false,
      showToPicker: false,
      showMonthPicker: false,
      search: null,
      showUnconfirmed: false,
      invoiceItemHeaders:
        'ship_date date meal_id description is_substitution uid first_name last_name plan stream city state order_type tod meal_size quantity unit_amount unit_discount coupon tax_type unit_taxable_amount unit_amount_tax total unconfirmed pickup'
          .split(' ')
          .map(i => ({
            value: i,
            text: i,
            ...(i === 'uid' ? {width: '30px'} : {})
          })),
      filters: [],
      loading: false,
      loadingComparison: false,
      loadingSummary: false,
      loadingComparisonSummary: false,
      summaryRequestId: 0,
      comparisonSummaryRequestId: 0,
      itemsRequestId: 0,
      comparisonItemsRequestId: 0,
      snackbar: false,
      snackbarText: ''
    }
  },
  mounted() {
    const routeHadMode = 'mode' in this.$route.query;
    this.syncToUrl({
      param: 'search', urlParam: 'search', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'mode', urlParam: 'mode', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'compareLastYear', urlParam: 'compare', initFromRoute: true,
      parseCallback: (v) => v === 'true',
      transformCallback: (v) => v ? 'true' : null
    });
    this.syncToUrl({
      param: 'filters', urlParam: 'filters', initFromRoute: true,
      paramWatchOptions: {deep: true},
      parseCallback: (v) => {
        if (!v) return [];
        try {
          return JSON.parse(v);
        } catch {
          return [];
        }
      },
      transformCallback: (v) => JSON.stringify(v)
    });
    if (!this.dates || this.dates.length === 0) {
      if (this.mode === 'month') {
        this.dates = [
          moment().startOf('month').format(DATE_FORMAT),
          moment().endOf('month').format(DATE_FORMAT)
        ];
      } else {
        const sunday = moment().startOf('week').format(DATE_FORMAT);
        this.dates = [sunday, sunday];
      }
    } else if (!routeHadMode) {
      this.mode = this.inferModeFromDates();
    }
    // nothing is fetched on load - the user picks a selection and hits Load
  },
  watch: {
    // changing an input never fetches - it just leaves the selection different
    // from what is loaded, which brings back the Load prompt
    mode: 'normalizeDates',
  },
  methods: {
    dateFormatted,
    formatCurrency,
    periodDaysFor(dateFrom, dateTo) {
      // one signup-bucket period = the selection length
      if (this.mode === 'month') {
        return moment(dateFrom).daysInMonth();
      }
      // dateTo is the last week's Sunday, so +7 includes that week
      return moment(dateTo).diff(moment(dateFrom), 'days') + 7;
    },
    loadData() {
      // collapse to the summary view: any loaded line items belong to the
      // previous selection
      this.invoicesVisible = false;
      this.invoiceItemsRaw = [];
      this.comparisonItemsRaw = [];
      const loaded = {
        params: this.loadParams,
        datesLabel: this.datesFormatted,
        comparisonLabel: this.comparisonDatesFormatted,
        // the inputs as loaded, so Cancel can put them back
        dates: [...this.dates],
        mode: this.mode,
        compare: this.compareLastYear,
        showUnconfirmed: this.showUnconfirmed,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        comparisonDates: this.comparisonDates
      };
      return Promise.all([this.fetchSummary(), this.fetchComparisonSummary()])
        .then(() => this.loaded = loaded);
    },
    cancelLoad() {
      // put the inputs back to what is on screen; the prompt hides itself once
      // the selection matches the loaded data again
      const {dates, mode, compare, showUnconfirmed} = this.loaded;
      this.mode = mode;
      this.dates = [...dates];
      this.compareLastYear = compare;
      this.showUnconfirmed = showUnconfirmed;
    },
    fetchSummary() {
      const {dateFrom, dateTo} = this;
      this.summary = null;
      if (!dateFrom || !dateTo) {
        return;
      }
      const requestId = ++this.summaryRequestId;
      this.loadingSummary = true;
      return api.get('v2/invoice/summary', {
        params: {
          dateFrom,
          dateTo,
          periodDays: this.periodDaysFor(dateFrom, dateTo),
          includeUnconfirmed: this.showUnconfirmed
        }
      })
        .then(r => {
          if (requestId === this.summaryRequestId) {
            this.summary = r.data;
          }
        })
        .catch(e => console.error('failed to fetch invoice summary', e))
        .finally(() => {
          if (requestId === this.summaryRequestId) {
            this.loadingSummary = false;
          }
        });
    },
    fetchComparisonSummary() {
      this.comparisonSummary = null;
      if (!this.compareLastYear || !this.comparisonDates) {
        return;
      }
      const [dateFrom, dateTo] = this.comparisonDates;
      const requestId = ++this.comparisonSummaryRequestId;
      this.loadingComparisonSummary = true;
      return api.get('v2/invoice/summary', {
        params: {
          dateFrom,
          dateTo,
          periodDays: this.periodDaysFor(dateFrom, dateTo),
          includeUnconfirmed: this.showUnconfirmed
        }
      })
        .then(r => {
          if (requestId === this.comparisonSummaryRequestId) {
            this.comparisonSummary = r.data;
          }
        })
        .catch(e => console.error('failed to fetch comparison summary', e))
        .finally(() => {
          if (requestId === this.comparisonSummaryRequestId) {
            this.loadingComparisonSummary = false;
          }
        });
    },
    fetchItems() {
      // line items must match the loaded summary, not a since-changed selection
      this.invoiceItemsRaw = [];
      if (!this.loaded) {
        return;
      }
      const {dateFrom, dateTo, showUnconfirmed} = this.loaded;
      const requestId = ++this.itemsRequestId;
      this.loading = true;
      return api.get('v2/invoice/items', {
        params: {
          dateFrom,
          dateTo,
          includeUnconfirmed: showUnconfirmed
        }
      })
        .then(r => {
          if (requestId === this.itemsRequestId) {
            this.invoiceItemsRaw = r.data;
          }
        })
        .catch(e => console.error('failed to fetch invoice items', e))
        .finally(() => {
          if (requestId === this.itemsRequestId) {
            this.loading = false;
          }
        });
    },
    fetchComparisonItems() {
      this.comparisonItemsRaw = [];
      if (!this.loaded || !this.loaded.compare || !this.loaded.comparisonDates) {
        return;
      }
      const [dateFrom, dateTo] = this.loaded.comparisonDates;
      const requestId = ++this.comparisonItemsRequestId;
      this.loadingComparison = true;
      return api.get('v2/invoice/items', {
        params: {
          dateFrom,
          dateTo,
          includeUnconfirmed: this.loaded.showUnconfirmed
        }
      })
        .then(r => {
          if (requestId === this.comparisonItemsRequestId) {
            this.comparisonItemsRaw = r.data;
          }
        })
        .catch(e => console.error('failed to fetch comparison items', e))
        .finally(() => {
          if (requestId === this.comparisonItemsRequestId) {
            this.loadingComparison = false;
          }
        });
    },
    toggleInvoices() {
      this.invoicesVisible = !this.invoicesVisible;
      if (this.invoicesVisible) {
        this.fetchItems();
        this.fetchComparisonItems();
      }
    },
    isSunday(d) {
      return moment(d).day() === 0
    },
    isAllowedToWeek(d) {
      return this.isSunday(d) && (!this.dateFrom || d >= this.dateFrom);
    },
    selectWeek(date) {
      this.showWeekPicker = false;
      this.dates = [date, date];
    },
    selectWeeksFrom(date) {
      this.showFromPicker = false;
      const to = this.dateTo && this.dateTo >= date ? this.dateTo : date;
      this.dates = [date, to];
    },
    selectWeeksTo(date) {
      this.showToPicker = false;
      this.dates = [this.dateFrom, date];
    },
    selectMonth(month) {
      this.showMonthPicker = false;
      const m = moment(month, 'YYYY-MM');
      this.dates = [
        m.clone().startOf('month').format(DATE_FORMAT),
        m.clone().endOf('month').format(DATE_FORMAT)
      ];
    },
    shiftSelection(direction) {
      if (!this.dateFrom) {
        return;
      }
      if (this.mode === 'month') {
        const m = moment(this.dateFrom).add(direction, 'month');
        this.dates = [
          m.clone().startOf('month').format(DATE_FORMAT),
          m.clone().endOf('month').format(DATE_FORMAT)
        ];
      } else {
        const spanDays = moment(this.dateTo).diff(moment(this.dateFrom), 'days') + 7;
        this.dates = [
          moment(this.dateFrom).add(direction * spanDays, 'days').format(DATE_FORMAT),
          moment(this.dateTo).add(direction * spanDays, 'days').format(DATE_FORMAT)
        ];
      }
    },
    closestSunday(date) {
      const m = moment(date);
      const day = m.day();
      if (day === 0) {
        return m.format(DATE_FORMAT);
      }
      return (day <= 3 ? m.subtract(day, 'days') : m.add(7 - day, 'days')).format(DATE_FORMAT);
    },
    normalizeDates() {
      if (!this.dateFrom) {
        return;
      }
      let next;
      if (this.mode === 'month') {
        const m = moment(this.dateFrom);
        next = [
          m.clone().startOf('month').format(DATE_FORMAT),
          m.clone().endOf('month').format(DATE_FORMAT)
        ];
      } else if (this.mode === 'week') {
        const sunday = this.closestSunday(this.dateFrom);
        next = [sunday, sunday];
      } else {
        const from = this.closestSunday(this.dateFrom);
        let to = this.closestSunday(this.dateTo);
        if (to < from) {
          to = from;
        }
        next = [from, to];
      }
      if (next[0] !== this.dateFrom || next[1] !== this.dateTo) {
        this.dates = next;
      }
    },
    inferModeFromDates() {
      if (this.dateFrom === this.dateTo) {
        return 'week';
      }
      const from = moment(this.dateFrom);
      if (from.date() === 1 && moment(this.dateTo).isSame(from.clone().endOf('month'), 'day')) {
        return 'month';
      }
      return 'weeks';
    },
    filterItems(items) {
      return items.filter(item => {
        // Apply all active filters
        for (const filter of this.filters) {
          if (filter.column && filter.values && filter.values.length > 0) {
            if (!filter.values.includes(item[filter.column])) {
              return false;
            }
          }
        }

        // Apply search filter
        if (this.search && this.search.trim()) {
          const searchTerm = this.search.toLowerCase();
          const searchableFields = Object.values(item);
          const found = searchableFields.some(field =>
            field && field.toString().toLowerCase().includes(searchTerm)
          );
          if (!found) {
            return false;
          }
        }

        return true;
      });
    },
    itemPayable(item) {
      const unitTaxableAmount = item.unit_taxable_amount !== undefined ? item.unit_taxable_amount : (item.unit_amount || 0);
      return (unitTaxableAmount + (item.unit_amount_tax || 0)) * (item.quantity || 0);
    },
    groupSales(items, field) {
      const groups = {};
      items.forEach(item => {
        // chef's choice orders often have no stream; order_type identifies them
        const label = (field === 'stream' && item.order_type === 'chefs_choice') ? "chef's choice"
          : item[field] || (item.order_type === 'shipping' ? '(shipping)' : '(none)');
        const group = groups[label] = groups[label] || {quantity: 0, sales: 0};
        group.quantity += item.quantity || 0;
        group.sales += this.itemPayable(item);
      });
      return groups;
    },
    computeStats(items) {
      const sum = values => values.reduce((total, v) => total + v, 0);
      const payable = this.itemPayable;

      // a customer is a billing account; its items (including shipping, which has
      // no uid) may span several profile uids (sub-accounts)
      const valueByCustomer = {};
      const uidsByCustomer = {};
      items.forEach(item => {
        const customer = item.billingUserId;
        if (!customer) {
          return;
        }
        valueByCustomer[customer] = (valueByCustomer[customer] || 0) + payable(item);
        if (item.uid) {
          (uidsByCustomer[customer] = uidsByCustomer[customer] || new Set()).add(item.uid);
        }
      });
      const profileBuckets = {'2': 0, '3': 0, '4': 0, '5+': 0};
      Object.values(uidsByCustomer).forEach(uids => {
        if (uids.size >= 5) {
          profileBuckets['5+']++;
        } else if (uids.size >= 2) {
          profileBuckets[String(uids.size)]++;
        }
      });
      const deliveriesByDay = [0, 0, 0, 0, 0, 0, 0];
      items.filter(o => o.order_type === 'shipping').forEach(item => {
        const shipDate = item.ship_date || item.date;
        if (shipDate) {
          deliveriesByDay[moment(shipDate).day()] += item.quantity || 0;
        }
      });

      const customerValues = Object.values(valueByCustomer).sort((a, b) => a - b);
      const customerCount = customerValues.length;
      const middle = Math.floor(customerCount / 2);
      const customerMedian = customerCount === 0 ? 0
        : customerCount % 2 === 1 ? customerValues[middle]
          : (customerValues[middle - 1] + customerValues[middle]) / 2;

      return {
        totalQuantity: sum(items.filter(o => o.order_type !== 'shipping').map(c => c.quantity || 0)),
        totalShipments: sum(items.filter(o => o.order_type === 'shipping').map(c => c.quantity || 0)),
        subtotal: sum(items.map(c => c.total || 0)),
        discount: sum(items.map(c => (c.unit_discount || 0) * (c.quantity || 0))),
        taxable: sum(items.map(c => {
          const unitTaxableAmount = c.unit_taxable_amount !== undefined ? c.unit_taxable_amount : (c.unit_amount || 0);
          return unitTaxableAmount * (c.quantity || 0);
        })),
        tax: sum(items.map(c => (c.unit_amount_tax || 0) * (c.quantity || 0))),
        invoiceTotal: sum(items.map(payable)),
        deliveriesByDay,
        monWedDeliveries: deliveriesByDay[1] + deliveriesByDay[3],
        customerCount,
        profileBuckets,
        customerAvg: customerCount ? sum(customerValues) / customerCount : 0,
        customerMedian,
        customerMin: customerCount ? customerValues[0] : 0,
        customerMax: customerCount ? customerValues[customerCount - 1] : 0,
        salesByPlan: this.groupSales(items, 'plan'),
        salesByStream: this.groupSales(items, 'stream'),
      };
    },
    formatChange(currentValue, previousValue) {
      if (!previousValue) {
        return {change: '—', changeClass: ''};
      }
      const pct = ((currentValue - previousValue) / previousValue) * 100;
      return {
        change: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`,
        changeClass: pct > 0 ? 'success--text' : pct < 0 ? 'error--text' : ''
      };
    },
    buildComparisonRows(current, previous) {
      const dayRows = moment.weekdays()
        .map((name, day) => ({label: `${name} Deliveries`, get: s => s.deliveriesByDay[day]}))
        .filter(row => row.get(current) || row.get(previous));
      const rows = [
        {label: 'Total Quantity', key: 'totalQuantity'},
        {label: 'Total Shipments', key: 'totalShipments'},
        ...dayRows,
        {label: 'Mon + Wed Deliveries', get: s => s.monWedDeliveries},
        {label: 'Customers Ordering', key: 'customerCount'},
        ...['2', '3', '4', '5+']
          .map(bucket => ({label: `Customers with ${bucket} profiles`, get: s => s.profileBuckets[bucket]}))
          .filter(row => row.get(current) || row.get(previous)),
        ...Object.keys(current.customerSignupBuckets || previous.customerSignupBuckets || {})
          .map(bucket => {
            const range = (current.customerSignupRanges || {})[bucket];
            return {
              label: `Signed Up ${bucket}${range ? ` (${range})` : ''}`,
              get: s => (s.customerSignupBuckets || {})[bucket] || 0
            };
          })
          .filter(row => row.get(current) || row.get(previous)),
        {label: 'Avg Customer Value', key: 'customerAvg', currency: true},
        {label: 'Median Customer Value', key: 'customerMedian', currency: true},
        {label: 'Min Customer Value', key: 'customerMin', currency: true},
        {label: 'Max Customer Value', key: 'customerMax', currency: true},
        {label: 'Subtotal', key: 'subtotal', currency: true},
        {label: 'Discount', key: 'discount', currency: true},
        {label: 'Taxable Amount', key: 'taxable', currency: true},
        {label: 'Tax', key: 'tax', currency: true},
        {label: 'Invoice Total', key: 'invoiceTotal', currency: true},
      ];
      return rows.map(row => {
        const currentValue = row.get ? row.get(current) : current[row.key];
        const previousValue = row.get ? row.get(previous) : previous[row.key];
        const format = row.currency ? formatCurrency : (v) => v;
        return {
          label: row.label,
          current: format(currentValue),
          previous: format(previousValue),
          ...this.formatChange(currentValue, previousValue)
        };
      });
    },
    mergeSales(...groupSets) {
      const merged = {};
      groupSets.forEach(groups => {
        Object.entries(groups || {}).forEach(([label, group]) => {
          const m = merged[label] = merged[label] || {quantity: 0, sales: 0};
          m.quantity += group.quantity;
          m.sales += group.sales;
        });
      });
      return merged;
    },
    salesRows(field) {
      // unlike the customer stats, the plan/stream breakdown includes
      // staff/pickup orders
      const current = this.mergeSales(this.stats[field], this.pickupStats[field]);
      const previous = this.showComparison
        ? this.mergeSales(this.comparisonStats[field], this.comparisonPickupStats[field])
        : {};
      const labels = [...new Set([...Object.keys(current), ...Object.keys(previous)])];
      return labels
        .map(label => {
          const c = current[label] || {quantity: 0, sales: 0};
          const p = previous[label] || {quantity: 0, sales: 0};
          return {
            label,
            quantity: c.quantity,
            sales: c.sales,
            previousSales: p.sales,
            ...this.formatChange(c.sales, p.sales)
          };
        })
        .sort((a, b) => b.sales - a.sales);
    },
    csvExport,
    exportFilename() {
      if (this.exportFile) {
        return this.exportFile;
      }
      const parts = [...this.dates, this.search].filter(s => !!s);
      return `invoice-${parts.join('-')}`;
    },
    addFilter() {
      this.filters.push({
        column: null,
        values: []
      });
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
    },
    availableColumnsForFilter(filterIndex) {
      const usedColumns = this.filters
        .map((f, index) => index !== filterIndex ? f.column : null)
        .filter(col => col !== null);

      return this.filterableColumns.filter(col => !usedColumns.includes(col.value));
    },
    getFilterValues(column) {
      if (!column) return [];

      const uniqueValues = [...new Set(this.invoiceItemsUnfiltered.map(i => i[column]))];
      return uniqueValues.filter(v => v !== null && v !== undefined && v !== '').sort();
    },
    copyToClipboard(text) {
      try {
        navigator.clipboard.writeText(text);
        this.snackbarText = 'Copied to clipboard';
        this.snackbar = true;
      } catch (e) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          this.snackbarText = 'Copied to clipboard';
          this.snackbar = true;
        } else {
          this.snackbarText = 'Failed to copy to clipboard';
          this.snackbar = true;
        }
      }
    },
  },
  computed: {
    loadParams() {
      return JSON.stringify({
        dates: [...this.dates].sort(),
        showUnconfirmed: this.showUnconfirmed,
        compareLastYear: this.compareLastYear
      });
    },
    hasData() {
      return !!this.summary;
    },
    showLoadPrompt() {
      if (this.loadingSummary || this.loadingComparisonSummary) {
        return true;
      }
      return this.loadParams !== (this.loaded && this.loaded.params);
    },
    // the tables describe the loaded data, not the (possibly changed) selection
    showComparison() {
      return !!(this.loaded && this.loaded.compare);
    },
    loadedDatesLabel() {
      return this.loaded ? this.loaded.datesLabel : '';
    },
    loadedComparisonLabel() {
      return this.loaded ? this.loaded.comparisonLabel : '';
    },
    datesFormatted() {
      if (!this.dateFrom) {
        return '';
      }
      if (this.mode === 'month') {
        return moment(this.dateFrom).format('MMMM YYYY');
      }
      if (this.dateFrom === this.dateTo) {
        return `Week of ${moment(this.dateFrom).format('MMM D, YYYY')}`;
      }
      return `Weeks of ${moment(this.dateFrom).format('MMM D')} – ${moment(this.dateTo).format('MMM D, YYYY')}`;
    },
    monthValue() {
      return this.dateFrom ? moment(this.dateFrom).format('YYYY-MM') : null;
    },
    monthFormatted() {
      return moment(this.dateFrom).format('MMMM YYYY');
    },
    weekCountHint() {
      if (!this.dateFrom || !this.dateTo) {
        return '';
      }
      const weeks = moment(this.dateTo).diff(moment(this.dateFrom), 'weeks') + 1;
      return `${weeks} week${weeks === 1 ? '' : 's'} selected`;
    },
    dateFrom() {
      return [...this.dates].sort()[0];
    },
    dateTo() {
      return [...this.dates].sort().reverse()[0];
    },
    comparisonDates() {
      if (!this.dateFrom) {
        return null;
      }
      if (this.mode === 'month') {
        const m = moment(this.dateFrom).subtract(1, 'year');
        return [
          m.clone().startOf('month').format(DATE_FORMAT),
          m.clone().endOf('month').format(DATE_FORMAT)
        ];
      }
      // same weeks last year: find the closest Sunday to the start date a year
      // earlier, then keep the same number of weeks
      const from = this.closestSunday(moment(this.dateFrom).subtract(1, 'year'));
      const weeks = moment(this.dateTo).diff(moment(this.dateFrom), 'weeks');
      return [from, moment(from).add(weeks, 'weeks').format(DATE_FORMAT)];
    },
    comparisonDatesFormatted() {
      const dates = this.comparisonDates;
      if (!dates) {
        return '';
      }
      if (this.mode === 'month') {
        return moment(dates[0]).format('MMMM YYYY');
      }
      if (dates[0] === dates[1]) {
        return `week of ${moment(dates[0]).format('MMM D, YYYY')}`;
      }
      return `weeks of ${moment(dates[0]).format('MMM D')} – ${moment(dates[1]).format('MMM D, YYYY')}`;
    },
    creditUsed() {
      return this.summary ? this.summary.creditUsed : 0;
    },
    invoiceItemsUnfiltered() {
      return this.invoiceItemsRaw;
    },
    invoiceItems() {
      return this.filterItems(this.invoiceItemsUnfiltered);
    },
    comparisonItems() {
      return this.filterItems(this.comparisonItemsRaw);
    },
    stats() {
      // once invoices are loaded, recompute client-side so filters/search
      // apply; otherwise use the server-side summary. staff/pickup accounts
      // are excluded here and summarized separately in pickupStats
      if (this.invoicesVisible && this.invoiceItemsRaw.length) {
        // the signup buckets need the customer DB, so they always come
        // from the server summary (filters don't apply to them)
        return {
          ...this.computeStats(this.invoiceItems.filter(i => !i.pickup)),
          customerSignupBuckets: this.summary && this.summary.customerSignupBuckets,
          customerSignupRanges: this.summary && this.summary.customerSignupRanges
        };
      }
      return this.summary || this.computeStats([]);
    },
    pickupStats() {
      if (this.invoicesVisible && this.invoiceItemsRaw.length) {
        return this.computeStats(this.invoiceItems.filter(i => i.pickup));
      }
      return (this.summary && this.summary.pickup) || this.computeStats([]);
    },
    comparisonStats() {
      if (this.invoicesVisible && this.comparisonItemsRaw.length) {
        return {
          ...this.computeStats(this.comparisonItems.filter(i => !i.pickup)),
          customerSignupBuckets: this.comparisonSummary && this.comparisonSummary.customerSignupBuckets,
          customerSignupRanges: this.comparisonSummary && this.comparisonSummary.customerSignupRanges
        };
      }
      return this.comparisonSummary || this.computeStats([]);
    },
    comparisonPickupStats() {
      if (this.invoicesVisible && this.comparisonItemsRaw.length) {
        return this.computeStats(this.comparisonItems.filter(i => i.pickup));
      }
      return (this.comparisonSummary && this.comparisonSummary.pickup) || this.computeStats([]);
    },
    showInvoicesLabel() {
      if (this.invoicesVisible) {
        return 'Hide Invoices';
      }
      const count = this.summary && this.summary.invoiceCount;
      return `Show Invoices${count ? ` (${count})` : ''}`;
    },
    comparisonRows() {
      const rows = this.buildComparisonRows(this.stats, this.comparisonStats);
      // credit comes from the unfiltered server summary, so only show it when
      // no filters are active (and it has no year-over-year comparison)
      if (!this.showComparison && this.filters.length === 0) {
        rows.push(
          {label: 'Credit Used', current: `-${formatCurrency(this.creditUsed)}`, previous: '', change: '', changeClass: ''},
          {label: 'Total Due', current: formatCurrency(this.stats.invoiceTotal - this.creditUsed), previous: '', change: '', changeClass: ''}
        );
      }
      return rows;
    },
    pickupComparisonRows() {
      return this.buildComparisonRows(this.pickupStats, this.comparisonPickupStats);
    },
    planSalesRows() {
      return this.salesRows('salesByPlan');
    },
    streamSalesRows() {
      return this.salesRows('salesByStream');
    },
    showPickupSection() {
      const hasData = stats => !!(stats && (stats.customerCount || stats.totalQuantity || stats.totalShipments));
      return hasData(this.pickupStats) || (this.showComparison && hasData(this.comparisonPickupStats));
    },
    dataAsTable() {
      return csvAsTable(this.invoiceItems, this.invoiceItemHeaders);
    },
    filterableColumns() {
      return this.invoiceItemHeaders.map(h => ({
        value: h.value,
        text: h.text
      }));
    },
  }
}
</script>


<style scoped>
.invoice-items-table ::v-deep td {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 0 !important;
}

.invoice-items-table ::v-deep th {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.comparison-table {
  width: auto;
  margin: 0 auto;
}

/* the summary tab's tables share one width: the container fits the widest
   table and the others stretch to match it */
.summary-tables {
  width: fit-content;
  margin: 0 auto;
}

.summary-tables .comparison-table,
.summary-tables .comparison-table ::v-deep table {
  width: 100%;
}

.comparison-table ::v-deep table {
  width: auto;
  margin: 0 auto;
}

.comparison-table ::v-deep td,
.comparison-table ::v-deep th {
  white-space: nowrap;
}
</style>
